import { Component, DestroyRef, ElementRef, computed, inject, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { LucideModule } from '../../../../../../shared/modules/lucide.module';
import { AgendamentoResponse, ErroAgendamento } from '../../../../models/agendamento.model';
import { AgendaMensalStore, DIAS_SEMANA, DiaCalendario } from '../../../../services/agenda-mensal.store';
import { AgendamentosService, dataEHoraDe, mapearErroAgendamento } from '../../../../services/agendamentos.service';
import { formatarData } from '../../../../services/solicitacoes.service';

/**
 * Troca da data e da hora de um agendamento, em "Meus agendamentos".
 *
 * É o modal "Agendar data e hora" da página do serviço com outro fim: o calendário é o mesmo
 * `AgendaMensalStore`, apontado para a agenda do **posto do agendamento** — o cidadão não troca
 * de posto, isso é do órgão. Por serem a mesma tela, os dois modais dividem a folha de estilos
 * do calendário (`.agenda-*`); a daqui só tem o que é do reagendamento.
 *
 * Diferente daquele, este modal **envia**: quem confirma aqui grava. O erro que dá para
 * resolver escolhendo de novo (`400`, `409`, `429`, falha) fica dentro do modal, com a escolha
 * mantida. O que encerra a tentativa (`422`, `404`) fecha o modal e sai por `recusado` — quem
 * mostra o alerta é a tela, DEPOIS do `close()`: o `<dialog>` aberto por `showModal()` fica na
 * top layer, e o SweetAlert abriria atrás dele, inerte.
 *
 * O que a tela mostra é uma fotografia: a vaga só é garantida no envio, e um horário tomado
 * nesse meio-tempo volta como 409.
 */
@Component({
  selector: 'app-modal-reagendamento',
  templateUrl: './modal-reagendamento.component.html',
  styleUrls: [
    '../../../../pages-servicos/modal-agendamento/modal-agendamento.component.css',
    './modal-reagendamento.component.css',
  ],
  imports: [LucideModule],
  providers: [AgendaMensalStore],
})
export class ModalReagendamentoComponent {
  private readonly agenda = inject(AgendaMensalStore);
  private readonly agendamentosService = inject(AgendamentosService);
  private readonly destroyRef = inject(DestroyRef);

  /** O agendamento já no horário novo. Emitido com o modal fechado. */
  readonly reagendado = output<AgendamentoResponse>();

  /** O envio foi recusado de um jeito que encerra a tentativa. Emitido com o modal fechado. */
  readonly recusado = output<ErroAgendamento>();

  readonly diasSemana = DIAS_SEMANA;

  readonly agendamento = signal<AgendamentoResponse | null>(null);
  readonly dataSelecionada = signal('');
  readonly horarioSelecionado = signal('');

  /** Um envio no ar: trava o segundo clique. */
  readonly enviando = signal(false);

  /** O erro do último envio que cabe resolver aqui — some na escolha seguinte. */
  readonly erro = signal<ErroAgendamento | null>(null);

  readonly carregando = this.agenda.carregando;
  readonly rotuloMes = this.agenda.rotuloMes;
  readonly diasDoMes = this.agenda.diasDoMes;
  readonly podeVoltarMes = this.agenda.podeVoltarMes;
  readonly podeAvancarMes = this.agenda.podeAvancarMes;
  readonly mesSemHorarios = this.agenda.mesSemHorarios;

  /** A agenda do posto não existe mais: o órgão o inativou. Não há "Tentar novamente" que resolva. */
  readonly postoIndisponivel = this.agenda.naoEncontrada;

  /** As demais falhas da leitura da agenda, que o "Tentar novamente" pode resolver. */
  readonly falhou = computed(() => this.agenda.falhou() && !this.agenda.naoEncontrada());

  private readonly dialogo = viewChild<ElementRef<HTMLDialogElement>>('dialogo');

  readonly horariosDoDia = computed<readonly string[]>(() => this.agenda.horariosDe(this.dataSelecionada()));

  readonly horarioAtual = computed(() => {
    const iso = this.agendamento()?.datAgendamentoSsv ?? '';

    return iso ? dataEHoraDe(iso) : '';
  });

  readonly dataFormatada = computed(() => {
    const iso = this.dataSelecionada();

    return iso ? formatarData(iso) : '';
  });

  /** A escolha é o horário que o agendamento já tem — com mais de uma vaga, a agenda o oferece. */
  readonly mesmoHorario = computed(() => {
    const atual = this.agendamento()?.datAgendamentoSsv ?? '';

    return (
      !!this.dataSelecionada() &&
      !!this.horarioSelecionado() &&
      atual.slice(0, 10) === this.dataSelecionada() &&
      atual.slice(11, 16) === this.horarioSelecionado()
    );
  });

  /** Só confirma horário que a agenda ainda oferece no mês carregado, e diferente do atual. */
  readonly podeConfirmar = computed(
    () =>
      !this.enviando() &&
      !this.postoIndisponivel() &&
      !!this.dataSelecionada() &&
      this.horariosDoDia().includes(this.horarioSelecionado()) &&
      !this.mesmoHorario(),
  );

  /**
   * Abre sem escolha feita, no mês do horário atual — é perto dele que a pessoa costuma querer o
   * novo. Cada abertura lê a agenda de novo.
   */
  abrir(agendamento: AgendamentoResponse): void {
    this.agendamento.set(agendamento);
    this.dataSelecionada.set('');
    this.horarioSelecionado.set('');
    this.erro.set(null);

    this.agenda.definirFonte(agendamento.codServicoSrv, agendamento.codLocalAtendimentoServicoLas);
    this.agenda.irParaMesDe(agendamento.datAgendamentoSsv.slice(0, 10));

    this.dialogo()?.nativeElement.showModal();
  }

  /** O `close()` nativo devolve o foco a quem o tinha antes de o modal abrir. */
  fechar(): void {
    const dialogo = this.dialogo()?.nativeElement;

    if (dialogo?.open) dialogo.close();

    this.aoFechar();
  }

  /**
   * Fechado — pelo botão, pelo envio ou pelo Esc, que só avisa pelo evento `close` —, o modal
   * solta o agendamento: o conteúdo sai da página até a próxima abertura.
   *
   * O evento chega depois do `close()`. Se o modal já tiver sido reaberto, não é com ele.
   */
  aoFechar(): void {
    if (this.dialogo()?.nativeElement.open) return;

    this.agendamento.set(null);
  }

  mesAnterior(): void {
    this.agenda.mesAnterior();
  }

  proximoMes(): void {
    this.agenda.proximoMes();
  }

  /** "Tentar novamente" da falha. */
  recarregar(): void {
    this.agenda.recarregar();
  }

  /** Trocar de dia zera o horário: a grade seguinte é de outra data. */
  selecionarDia(dia: DiaCalendario): void {
    if (dia.indisponivel || dia.dia === null) return;

    this.dataSelecionada.set(dia.iso);
    this.horarioSelecionado.set('');
    this.erro.set(null);
  }

  selecionarHorario(horario: string): void {
    this.horarioSelecionado.set(horario);
    this.erro.set(null);
  }

  confirmar(): void {
    const agendamento = this.agendamento();

    if (!agendamento || !this.podeConfirmar()) return;

    this.enviando.set(true);
    this.erro.set(null);

    this.agendamentosService
      .reagendar(agendamento.desProtocoloSsv, this.dataSelecionada(), this.horarioSelecionado())
      .pipe(
        finalize(() => this.enviando.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        // ⚠️ Fecha ANTES de emitir: quem escuta abre um alerta.
        next: (reagendado) => {
          this.fechar();
          this.reagendado.emit(reagendado);
        },
        error: (erro: unknown) => this.aoFalhar(mapearErroAgendamento(erro, 'reagendar')),
      });
  }

  private aoFalhar(erro: ErroAgendamento): void {
    switch (erro.tipo) {
      // O horário lotou, ou o órgão mexeu no agendamento no mesmo instante: a grade que a
      // pessoa vê está velha.
      case 'vaga':
        this.horarioSelecionado.set('');
        this.erro.set(erro);
        this.agenda.recarregar();
        return;

      // O prazo terminou, o agendamento mudou ou o posto fechou: não há o que escolher de novo.
      case 'estado':
      case 'nao-encontrado':
        this.fechar();
        this.recusado.emit(erro);
        return;

      // O interceptor já encerrou a sessão, e a aba mostra o aviso dela.
      case 'sessao':
        this.fechar();
        return;

      default:
        this.erro.set(erro);
    }
  }
}
